import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Clock, AlertCircle, Phone, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { openingHours, practiceInfo } from '../data/mock';

const OpeningHours = () => {
  const days = [
    { name: 'Montag', data: openingHours.monday },
    { name: 'Dienstag', data: openingHours.tuesday },
    { name: 'Mittwoch', data: openingHours.wednesday },
    { name: 'Donnerstag', data: openingHours.thursday },
    { name: 'Freitag', data: openingHours.friday }
  ];

  return (
    <section id="sprechzeiten" className="py-20 bg-gradient-to-br from-red-50 via-white to-red-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Sprechzeiten
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Wir sind für Sie da - mit flexiblen Sprechzeiten und 
            offenen Sprechstunden für Ihre Gesundheit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Regular Hours */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-red-100">
            <CardHeader className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center text-xl">
                <Clock className="h-6 w-6 mr-3" />
                Reguläre Sprechzeiten
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {days.map((day, index) => (
                  <div 
                    key={index}
                    className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0"
                  >
                    <span className="font-medium text-gray-900">{day.name}</span>
                    <div className="text-right">
                      <div className="text-gray-700">
                        {day.data.morning}
                      </div>
                      {day.data.afternoon !== 'geschlossen' && (
                        <div className="text-gray-700">
                          {day.data.afternoon}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Emergency Hours & Actions */}
          <div className="space-y-6">
            <Card className="bg-orange-50 border-orange-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center text-xl">
                  <AlertCircle className="h-6 w-6 mr-3" />
                  Offene Sprechstunden
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 font-medium text-lg mb-4">
                  {openingHours.emergency}
                </p>
                <p className="text-gray-600 text-sm">
                  Für akute Beschwerden ohne Voranmeldung. 
                  Bitte haben Sie Verständnis für eventuelle Wartezeiten.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-red-100">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Termine & Kontakt
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center text-gray-600">
                    <Phone className="h-5 w-5 mr-3 text-red-600" />
                    <div>
                      <p className="font-medium text-gray-900">Telefon</p>
                      <p>{practiceInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-5 w-5 mr-3 text-red-600" />
                    <div>
                      <p className="font-medium text-gray-900">Online Buchung</p>
                      <p className="text-sm">24/7 verfügbar</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  <Button 
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                    onClick={() => window.open(practiceInfo.contact.booking_url, '_blank')}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Online Termin buchen
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => window.location.href = `tel:${practiceInfo.contact.phone}`}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Anrufen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-red-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Wichtige Hinweise
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Terminvereinbarung</h4>
                <p className="text-gray-600 text-sm">
                  Bitte vereinbaren Sie Termine telefonisch oder online. 
                  Für versäumte Termine wird ein Ausfallhonorar in Rechnung gestellt, wenn der gebuchte Termin nicht mindestens 24 Stunden vorher abgesagt wurde.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Wartezeiten</h4>
                <p className="text-gray-600 text-sm">
                  Wir bemühen uns um pünktliche Termine. Bei Notfällen kann es 
                  zu Verzögerungen kommen - vielen Dank für Ihr Verständnis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OpeningHours;